import type { TrpcClient } from "./trpc/client.js";

/**
 * Wire-shaped view types for the console, derived from the live
 * `support.*` procedure return types via the typed tRPC client. Deriving
 * from `Awaited<ReturnType<…>>` (rather than re-importing the API's
 * internal `AiInvocationRecord`) means these types carry exactly what
 * crosses the wire — tRPC's serialization widening included — and they
 * track the contract automatically if S-04 ever changes shape.
 */
export type InvocationListResult = Awaited<
  ReturnType<TrpcClient["support"]["listInvocations"]["query"]>
>;

/** One summary row in the list (no raw response). */
export type InvocationSummary = InvocationListResult["items"][number];

export type InvocationDetail = Awaited<
  ReturnType<TrpcClient["support"]["getInvocation"]["query"]>
>;

/** The full row (with raw response) the detail view renders. */
export type InvocationRecord = InvocationDetail["invocation"];

/** Recomputed parse outcome (ok / failed / no-response). */
export type ParseResult = InvocationDetail["parse"];

/** A submitted photo + its presigned GET URL. */
export type InvocationPhoto = InvocationDetail["photos"][number];

/**
 * One parsed suggestion the review viewer drives off — its confidence,
 * the photos it cited, and (when present) the per-photo bounding-box
 * regions the model used. `proposedValue` is the parsed kind-specific
 * value, passed through as `unknown`.
 */
export type InvocationSuggestion = InvocationDetail["suggestions"][number];

/** One per-photo bounding-box region on a suggestion. */
export type SuggestionRegion = NonNullable<
  InvocationSuggestion["regions"]
>[number];

/**
 * UR-6 — the support-only derived-image payload `getInvocation` returns
 * for a union-path panel scan: one **read image per breaker photo** (the
 * whole-panel crop the model actually read) with the geometry/column
 * overlay, keyed by `sourcePhotoId`. `null` for a replayed/stub/flag-off
 * invocation (no manifest) — the UI then shows "no derived images". All
 * URLs are minted inside the staff procedure; nothing here is reachable
 * from a user-facing route (the keys live under the `derived/` namespace).
 */
export type DerivedGallery = NonNullable<InvocationDetail["derived"]>;

/** One source photo's derived view: its whole-panel read image. */
export type DerivedPhoto = DerivedGallery["photos"][number];

/** The per-photo read image — the union crop sent to the model (`null` on a legacy row). */
export type DerivedReadImage = NonNullable<DerivedPhoto["read"]>;

/**
 * TC-7 — the AI Scan Test-Case Corpus view types, derived (like the
 * invocation types above) from the live `support.*` procedure returns so
 * they track the contract automatically.
 */
export type TestCaseListResult = Awaited<
  ReturnType<TrpcClient["support"]["listTestCases"]["query"]>
>;

/** One summary row in the corpus list (no baseline reads). */
export type TestCaseSummary = TestCaseListResult["items"][number];

export type TestCaseDetail = Awaited<
  ReturnType<TrpcClient["support"]["getTestCase"]["query"]>
>;

/** The immutable case record (name, notes, baseline provenance, runs). */
export type TestCaseRecord = TestCaseDetail["testCase"];

/** One durable baseline photo + its presigned GET URL. */
export type TestCasePhoto = TestCaseDetail["photos"][number];

/** One run resolved to the invocation it produced (or `null` if pending). */
export type TestCaseRunSummary = TestCaseDetail["runs"][number];

export type InvocationKind = InvocationSummary["kind"];
export type InvocationOutcome = InvocationSummary["outcome"];

/** The three scan kinds, in the order S-04's input enum lists them. */
export const INVOCATION_KINDS: readonly InvocationKind[] = [
  "panel-scan",
  "room-device-scan",
  "nameplate-ocr",
];

/**
 * The three recorded outcomes plus the synthetic "processing" arm — an
 * in-flight scan job the server projects into the list (no invocation
 * row exists until the provider call completes).
 */
export const INVOCATION_OUTCOMES: readonly InvocationOutcome[] = [
  "success",
  "parse-failed",
  "provider-error",
  "processing",
];
